import { formatDateTime } from '@/components/BatchList';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { getPlaceholderAvatar } from '@/core/avatar';
import { execute } from '@/graphql/execute';
import { useAlert } from '@/providers/AlertProvider';
import {
  organizationInvitationsQuery,
  resendInvitationMutation,
  revokeInvitationMutation,
} from '@/queries/organization.query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface InvitationsListProps {
  organizationId: string;
}

export function InvitationsList({ organizationId }: InvitationsListProps) {
  const { t } = useTranslation('users');
  const { data: invitationsData } = useQuery({
    queryKey: ['invitations', organizationId],
    queryFn: () => execute(organizationInvitationsQuery),
  });

  const queryClient = useQueryClient();
  const { openAlert } = useAlert();

  const revoke = useMutation({
    mutationFn: async ({ invitationId }: { invitationId: string }) => {
      // TODO: Uncomment when GraphQL types are regenerated
      return execute(revokeInvitationMutation, {
        id: invitationId,
      });
    },
    onSuccess: () => {
      toast.success(t('invitations.revokedSuccess'));
      queryClient.invalidateQueries({ queryKey: ['invitations', organizationId] });
    },
    onError: (error: any) => {
      toast.error(error.message || t('invitations.failedRevoke'));
    },
  });

  const resend = useMutation({
    mutationFn: async ({ invitationId }: { invitationId: string }) => {
      return execute(resendInvitationMutation, {
        id: invitationId,
      });
    },
    onSuccess: () => {
      toast.success(t('invitations.resentSuccess'));
      queryClient.invalidateQueries({ queryKey: ['invitations', organizationId] });
    },
    onError: (error: any) => {
      toast.error(error.message || t('invitations.failedResend'));
    },
  });

  const invitations = invitationsData?.invitations || [];

  // Filter active invitations (not accepted and not expired)
  const activeInvitations = invitations.filter(
    (invitation) => !invitation.acceptedAt && new Date(invitation.expiresAt) > new Date(),
  );

  const _handleRevokeInvitation = (invitationId: string, email: string) => {
    openAlert({
      title: t('invitations.revokeTitle'),
      content: t('invitations.revokeConfirm', { email }),
      actions: [
        {
          label: t('invitations.revoke'),
          variant: 'destructive',
          onClick: () => revoke.mutate({ invitationId }),
        },
      ],
    });
  };

  if (activeInvitations.length === 0) return null;

  return (
    <div className='mt-8'>
      <h3 className='text-lg font-semibold mb-2'>{t('invitations.title')}</h3>
      <div className='rounded-md border bg-white dark:bg-stone-900 dark:border-stone-800'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('invitations.email')}</TableHead>
              <TableHead>{t('invitations.role')}</TableHead>
              <TableHead>{t('invitations.invited')}</TableHead>
              <TableHead>{t('invitations.expires')}</TableHead>
              <TableHead className='w-[50px]'></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {activeInvitations.map((invitation) => (
              <TableRow key={invitation.id}>
                <TableCell className='font-medium'>
                  <div className='flex items-center gap-4'>
                    <img
                      src={getPlaceholderAvatar(invitation.email || invitation.id || '')}
                      alt={invitation.email}
                      className='h-8 w-8 rounded-full object-cover'
                    />
                    {invitation.email}
                  </div>
                </TableCell>
                <TableCell>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      invitation.role === 'ADMIN'
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                        : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200'
                    }`}
                  >
                    {invitation.role}
                  </span>
                </TableCell>
                <TableCell>{formatDateTime(invitation.createdAt)}</TableCell>
                <TableCell>{formatDateTime(invitation.expiresAt)}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant='ghost' className='h-8 w-8 p-0'>
                        <span className='sr-only'>{t('invitations.openMenu')}</span>
                        <MoreHorizontal className='h-4 w-4' />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                      <DropdownMenuLabel>{t('invitations.actions')}</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => resend.mutate({ invitationId: invitation.id })}
                        disabled={resend.isPending}
                      >
                        {t('invitations.resendInvitation')}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => _handleRevokeInvitation(invitation.id, invitation.email)}
                        disabled={revoke.isPending}
                        className='text-red-600'
                      >
                        {t('invitations.revokeInvitation')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
